// AddDiscount.jsx
import React, { useState, useEffect } from "react";
import {  Spin } from "antd";
import Swal from "sweetalert2";
import Select from "react-select";
import PropTypes from "prop-types";
import Axios from "axios";
import { API_BASE_URL, GET_USER_STORES } from "../../Config"; // your config import

const AddDiscount = ({ isOpen, onClose, handleDiscountsView }) => {
  const [userData, setUserData] = useState(null);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [discountName, setDiscountName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isPercentage, setIsPercentage] = useState(false); // 0 = Fixed, 1 = Percentage
  const [value, setValue] = useState(""); // discount value
  const [minProductValue, setMinProductValue] = useState("");
  const [maxDiscountValue, setMaxDiscountValue] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [loadingBtn, setLoadingBtn] = useState(false);
  const [productLoading, setProductLoading] = useState(false);

  useEffect(() => {
    const userStr = sessionStorage.getItem("userData");
    if (userStr) {
      setUserData(JSON.parse(userStr));
    }
  }, []);

  // Load stores (same as listing screen)
  useEffect(() => {
    if (!userData?.OrgId || !userData?.UserId) return;

    Axios.get(
      `${API_BASE_URL}${GET_USER_STORES}?OrgId=${userData.OrgId}&UserId=${userData.UserId}`
    )
      .then((res) => {
        if (res.data?.ResultData) {
          const opts = res.data.ResultData.map((s) => ({
            value: s.StoreId || s.Id,
            label: s.Name,
          }));
          setStores([{ value: 0, label: "All Stores" }, ...opts]);
        }
      })
      .catch((err) => console.error("Stores fetch error:", err));
  }, [userData]);

  // Load categories from sessionStorage
  useEffect(() => {
    const catStr = sessionStorage.getItem("filteredDDLData_CATEGORY");
    if (catStr) {
      const catData = JSON.parse(catStr);
      const opts = catData.map((c) => ({
        value: c.ItemId,
        label: c.ItemValue || c.DisplayValue,
      }));
      setCategories([{ value: 0, label: "All Categories" }, ...opts]);
    }
  }, []);

  // Load products when store or category changes
  useEffect(() => {
    if (!userData?.OrgId || !selectedStore?.value) {
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      setProductLoading(true);
      try {
        const params = {
          OrgId: userData.OrgId,
          StoreId: selectedStore.value,
          CategoryId: selectedCategory?.value || 0,
          ProductId: 0,
        };

        const res = await Axios.get(`${API_BASE_URL}Product/ProductsDDL`, { params });

        if (res.data?.ResultData) {
          setProducts(res.data.ResultData);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Products fetch failed:", err);
        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    };

    fetchProducts();
  }, [userData, selectedStore?.value, selectedCategory?.value]);

  const handleSubmit = async () => {
    if (!selectedStore?.value || !selectedProduct?.value || !discountName || !quantity || !fromDate || !toDate || !value) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    setLoadingBtn(true);

    const payload = {
      Name: discountName.trim(),
      OrgId: userData.OrgId,
      StoreId: selectedStore.value,
      ProductId: selectedProduct.value,
      Quantity: parseInt(quantity, 10),
      FromDate: fromDate,
      ToDate: toDate,
      IsPercentage: isPercentage ? 1 : 0,
      MaxDiscountValue: parseFloat(maxDiscountValue) || 0,
      MinProductValue: parseFloat(minProductValue) || 0,
      Value: parseFloat(value),
      IsActive: isActive ? 1 : 0,
      CreatedBy: userData.UserId,
    };

    try {
      const res = await Axios.post(
        `${API_BASE_URL}Settings/InsertStoreProdDiscount`,
        payload
      );

      if (res.data?.Status === true || res.data?.message?.includes("success")) {
        Swal.fire({
          title: "Success!",
          text: "Discount created successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          handleDiscountsView(); // refresh parent list
          onClose();             // close offcanvas
          resetForm();
        });
      } else {
        Swal.fire("Error", res.data?.Message || "Failed to create discount", "error");
      }
    } catch (err) {
      console.error("Create discount error:", err);
      Swal.fire("Error", "Server error while creating discount", "error");
    } finally {
      setLoadingBtn(false);
    }
  };

  const resetForm = () => {
    setDiscountName("");
    setQuantity("");
    setFromDate("");
    setToDate("");
    setIsPercentage(false);
    setValue("");
    setMinProductValue("");
    setMaxDiscountValue("");
    setIsActive(true);
    setSelectedProduct(null);
    
  };

  return (
    <>
      <style>{`
        .offcanvas {
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          visibility: hidden;
          will-change: transform;
        }
        .offcanvas.show {
          visibility: visible;
        }
        .offcanvas-backdrop {
          opacity: 0;
          transition: opacity 0.3s ease;
          scrollbar-gutter: stable;
          z-index: 1040;
        }
        .offcanvas-backdrop.show {
          opacity: 0.5;
        }
        @media (max-width: 576px)    { #offcanvasRightAddDiscount { width: 90% !important; } }
        @media (min-width: 577px) and (max-width: 992px)  { #offcanvasRightAddDiscount { width: 55% !important; } }
        @media (min-width: 993px) and (max-width: 1440px) { #offcanvasRightAddDiscount { width: 35% !important; } }
        @media (min-width: 1441px)   { #offcanvasRightAddDiscount { width: 20% !important; } }
      `}</style>

      {loadingBtn && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 2050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Creating discount..." />
        </div>
      )}

      <div
        className={`offcanvas-backdrop fade ${isOpen ? "show" : ""}`}
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
        onClick={onClose}
      />

      <div
        id="offcanvasRightAddDiscount"
        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
        style={{ pointerEvents: isOpen ? "auto" : "none" }}
      >
        <div className="offcanvas-header">
          <h4>Create Product Discount</h4>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              resetForm();
              onClose();
            }}
          />
        </div>
         <hr className="m-0" />
        <div className="offcanvas-body">
          <form>
            <div className="row g-3">
              {/* Store */}
              <div className="col-md-6">
                <label className="form-label">
                  Store <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder="Select Store"
                  value={selectedStore}
                  onChange={setSelectedStore}
                  options={stores}
                  disabled={loadingBtn}
                />
              </div>

              {/* Category (optional filter for products) */}
              <div className="col-md-6 d-none">
                <label className="form-label">Category</label>
                <Select
                  placeholder="All Categories"
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  options={categories}
                  allowClear
                  disabled={loadingBtn}
                />
              </div>

              {/* Product */}
              <div className="col-md-6">
                <label className="form-label">
                  Product <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder={productLoading ? "Loading products..." : "Select Product"}
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                  options={products.map((p) => ({
                    value: p.Id,
                    label: p.ProductName,
                  }))}
                  loading={productLoading}
                  disabled={loadingBtn || !selectedStore}
                />
              </div>

              {/* Discount Name */}
              <div className="col-md-6">
                <label className="form-label">
                  Discount Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={discountName}
                  onChange={(e) => setDiscountName(e.target.value)}
                  placeholder=" e.g. Summer Sale"
                  disabled={loadingBtn}
                  required
                />
              </div>

              {/* Quantity */}
              <div className="col-md-6">
                <label className="form-label">
                  Quantity <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="1"
                  disabled={loadingBtn}
                  required
                />
              </div>

              {/* From Date & To Date */}
              <div className="col-md-6">
                <label className="form-label">
                  From  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loadingBtn}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  To  <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loadingBtn}
                  required
                />
              </div>

              {/* Is Percentage */}
              <div className="col-md-6">
                <label className="form-label">Discount Type</label>
                <Select
                  value={isPercentage ? "percentage" : "fixed"}
                  onChange={(val) => setIsPercentage(val === "percentage")}
                  options={[
                    { value: "fixed", label: "Fixed Amount" },
                    { value: "percentage", label: "Percentage" },
                  ]}
                  disabled={loadingBtn}
                />
              </div>

              {/* Value */}
              <div className="col-md-6">
                <label className="form-label">
                  Discount  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  step="0.01"
                  min="0"
                  disabled={loadingBtn}
                  required
                />
              </div>

              {/* Min & Max */}
              <div className="col-md-6 d-none">
                <label className="form-label">Min Product Value</label>
                <input
                  type="number"
                  className="form-control"
                  value={minProductValue}
                  onChange={(e) => setMinProductValue(e.target.value)}
                  step="0.01"
                  min="0"
                  disabled={loadingBtn}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Max Discount </label>
                <input
                  type="number"
                  className="form-control"
                  value={maxDiscountValue}
                  onChange={(e) => setMaxDiscountValue(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder="Enter Only Percentage"
                  disabled={loadingBtn}
                />
              </div>

              {/* Active */}
              <div className="col-12 d-none">
                <label className="form-label">Status</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={loadingBtn}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                disabled={loadingBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={loadingBtn}
                onClick={handleSubmit}
              >
                {loadingBtn ? "Saving..." : "Create Discount"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

AddDiscount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleDiscountsView: PropTypes.func.isRequired, 
};

export default AddDiscount;